import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customerService } from "services";
import { CustomerForm, CustomerQuery, CustomerUpdatePayload } from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type CustomerListKey = ["customers"] | ["customers", CustomerQuery];
type CustomerDetailKey = ["customers", "detail", number | string];

const customerKeys = {
  all: ["customers"] as const,
  list: (params?: CustomerQuery): CustomerListKey =>
    params ? ["customers", params] : ["customers"],
  detail: (id: number | string): CustomerDetailKey => [
    "customers",
    "detail",
    id,
  ],
};

export function useCustomer() {
  const queryClient = useQueryClient();

  const fetchList = (params?: CustomerQuery, enabled = true) => {
    return useQuery({
      queryKey: customerKeys.list(params),
      queryFn: () => customerService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: data?.totalPage ?? 1,
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: customerKeys.detail(id),
      queryFn: () => customerService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createCustomer = useMutation({
    mutationFn: (payload: CustomerForm) => customerService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(res?.message ?? "Tạo khách hàng thành công");
      }
      queryClient.invalidateQueries({ queryKey: customerKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo khách hàng thất bại");
      console.error("createCustomer failed", err);
    },
  });

  const updateCustomer = useMutation({
    mutationFn: (payload: CustomerUpdatePayload) =>
      customerService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Cập nhật khách hàng thành công");
      }
      queryClient.invalidateQueries({ queryKey: customerKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật khách hàng thất bại");
      console.error("updateCustomer failed", err);
    },
  });

  const deleteCustomer = useMutation({
    mutationFn: (id: number | string) => customerService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Xoá khách hàng thành công");
      }
      queryClient.invalidateQueries({ queryKey: customerKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá khách hàng thất bại");
      console.error("deleteCustomer failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createCustomer,
    updateCustomer,
    deleteCustomer,
  };
}
